export interface NavItem {
  id: string;
  label: string;
  path: string;
  order: number;
  isActive: boolean;
}

export interface HeroSlide {
  id: string;
  image: string;
  title: string;
  subtitle: string;
  ctaText?: string;
  ctaLink?: string;
  order: number;
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  category: ProductCategory;
  images: string[];
  description: string;
  shortDescription: string;
  origin: string;
  packaging: string[];
  shelfLife: string;
  moq: string;
  exportAvailability: string[];
  qualityInfo: string;
  specifications: Record<string, string>;
  isActive: boolean;
  order: number;
}

export enum ProductCategory {
  GRAINS = 'grains',
  FRUITS = 'fruits',
  VEGETABLES = 'vegetables',
  PICKLES = 'pickles',
  GRAIN_POWDERS = 'grain-powders'
}

export interface Category {
  id: ProductCategory;
  name: string;
  description: string;
  image: string;
  productCount: number;
}

export interface Certification {
  id: string;
  name: string;
  issuer: string;
  issueDate: string;
  expiryDate: string;
  image: string;
  description: string;
  order: number;
}

export interface QualityStandard {
  id: string;
  title: string;
  description: string;
  icon: string;
}

export interface Country {
  id: string;
  name: string;
  flag: string;
  continent: string;
  isActive: boolean;
}

export interface WorkflowStep {
  id: string;
  step: number;
  title: string;
  description: string;
  icon: string;
}

export interface CompanyInfo {
  name: string;
  logo: string;
  favicon: string;
  address: string;
  email: string;
  phone: string;
  whatsapp: string;
  socialMedia: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    twitter?: string;
  };
}

export interface HomeContent {
  heroSlides: HeroSlide[];
  sections: {
    products: SectionContent;
    quality: SectionContent;
    international: SectionContent;
    certifications: SectionContent;
    blog: SectionContent;
  };
  seo: SEOContent;
}

export interface AboutContent {
  title: string;
  description: string;
  mission: string;
  vision: string;
  coreValues: Array<{
    title: string;
    description: string;
    icon: string;
  }>;
  image: string;
  seo: SEOContent;
}

export interface ProductsContent {
  title: string;
  description: string;
  categories: Category[];
  products: Product[];  
  seo: SEOContent;
}

export interface InternationalContent {
  title: string;
  description: string;
  countries: Country[];
  workflowSteps: WorkflowStep[];
  logisticsPartners: Array<{
    name: string;
    logo: string;
    description: string;
  }>;
  seo: SEOContent;
}

export interface QualityContent {
  title: string;
  description: string;
  qualityProcess: Array<{
    title: string;
    description: string;
    image: string;
  }>;
  hygieneStandards: Array<{
    title: string;
    description: string;
    icon: string;
  }>;
  packagingTypes: Array<{
    name: string;
    description: string;
    image: string;
    specifications: string;
  }>;
  seo: SEOContent;
}

export interface CertificationsContent {
  title: string;
  description: string;
  certifications: Certification[];
  seo: SEOContent;
}

export interface ContactContent {
  title: string;
  description: string;
  address: string;
  email: string;
  phone: string;
  whatsapp: string;
  mapLocation: {
    lat: number;
    lng: number;
    address: string;
  };
  businessHours: Array<{
    day: string;
    hours: string;
  }>;
  seo: SEOContent;
}

export interface SectionContent {
  title: string;
  description: string;
  isActive: boolean;
}

export interface SEOContent {
  title: string;
  description: string;
  keywords: string[];
  ogImage?: string;
}

export interface FooterLinks {
  companyLinks: NavItem[];
  quickLinks: NavItem[];
  productLinks: Array<{
    label: string;
    path: string;
    category: ProductCategory;
  }>;
  socialMedia: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    whatsapp?: string;
  };
  copyright: string;
}

export interface Navigation {
  header: NavItem[];
  footer: FooterLinks;
}

export interface PageContent {
  home: HomeContent;
  about: AboutContent;
  products: ProductsContent;
  international: InternationalContent;
  quality: QualityContent;
  certifications: CertificationsContent;
  contact: ContactContent;
}

export interface WebsiteContent {
  companyInfo: CompanyInfo;
  navigation: Navigation;
  pages: PageContent;
  updatedAt: string;
  version: string;
}

export interface InquiryFormValues {
  name: string;
  email: string;
  phone: string;
  country: string;
  product: string;
  quantity: string;
  message: string;
}

// ============================================
// NEW: User Management Types (for Admin)
// ============================================

// ============================================
// User Management Types (for Admin)
// ============================================

export enum UserRole {
  ADMIN = 'ROLE_ADMIN',
  DIRECTOR = 'ROLE_DIRECTOR',
  MANAGER = 'ROLE_MANAGER',
  USER = 'ROLE_USER',
  // Backward compatibility
  VIEWER = 'ROLE_USER'  // Alias for USER (optional)
}

export interface User {
  id: string;
  username: string;
  email: string;
  phoneNumber: string;
  fullName: string;
  enabled: boolean;
  twoFactorEnabled: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  roles: UserRole[];  // Will now include ADMIN, DIRECTOR, MANAGER, USER
  createdAt: string;
  lastLoginAt: string | null;
  profileImageUrl?: string;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  phoneNumber: string;
  fullName: string;
  password: string;
  roles: UserRole[];
  enabled?: boolean;
  twoFactorEnabled?: boolean;
}

export interface UpdateUserRequest {
  email?: string;
  phoneNumber?: string;
  fullName?: string;
  password?: string;
  roles?: UserRole[];
  enabled?: boolean;
  twoFactorEnabled?: boolean;
}

export interface UserFilters {
  role?: UserRole;
  status?: 'active' | 'locked';
  search?: string;
}
// ============================================
// NEW: Authentication Types
// ============================================

export interface LoginRequest {
  login: string; // username or email
  password: string;
}

export interface VerifyOtpRequest {
  userId: string;
  otpCode: string;
  purpose?: '2FA_LOGIN' | 'PASSWORD_RESET' | 'EMAIL_VERIFICATION';
}

export interface AuthResponse {
  success: boolean;
  requiresTwoFactor: boolean;
  message?: string;
  token?: string;
  refreshToken?: string;
  userId?: string;
  username?: string;
  email?: string;
  roles?: UserRole[];
  twoFactorSecret?: string;
  twoFactorQrCode?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  otpCode: string;
  newPassword: string;
}

// ============================================
// NEW: API Response Types
// ============================================

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
  timestamp?: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

// ============================================
// NEW: Gallery Types (for Admin)
// ============================================

export interface Gallery {
  id: string;
  name: string;
  description: string;
  active: boolean;
  items: GalleryItem[];
  createdAt: string;
  updatedAt: string;
}

export interface GalleryItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  thumbnailUrl: string;
  linkUrl?: string;
  altText?: string;
  type: 'IMAGE' | 'VIDEO' | 'BANNER' | 'SLIDE' | 'LOGO' | 'ICON';
  orderIndex: number;
  active: boolean;
  metadata?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGalleryItemRequest {
  title?: string;
  description?: string;
  linkUrl?: string;
  altText?: string;
  type: 'IMAGE' | 'VIDEO' | 'BANNER' | 'SLIDE' | 'LOGO' | 'ICON';
  orderIndex?: number;
  active?: boolean;
  metadata?: string;
}